/**
 * PublicView — publik storskärm för talarstödet.
 * Minimal mörkt tema, lyssnar på currentBlock via onSnapshot.
 * Kräver ingen inloggning (läsrättigheter på workshops-dokumentet).
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { db } from '../../../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { useCompanyLogo } from '../../../../hooks/useQRLogo';

function toEmbedUrl(url) {
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) {
    const id = m[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0`;
  }
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1&muted=1&loop=1&background=1`;
  return null;
}

export default function PublicView({ sessionId }) {
  const [intro,       setIntro]       = useState(undefined);
  const [session,     setSession]     = useState(null);
  const companyLogo = useCompanyLogo();

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setIntro(d.intro || null);
        setSession(d);
      } else {
        setIntro(null);
        setSession(null);
      }
    });
    return unsub;
  }, [sessionId]);

  const currentBlock = intro?.currentBlock ?? 0;
  const block = intro?.blocks?.[currentBlock];
  const isWelcomeSlide = currentBlock === 0;
  const wifiSsid        = session?.wifiSsid || '';
  const wifiPassword    = session?.wifiPassword || '';
  const imageUrl        = session?.imageUrl || '';
  const videoUrl        = session?.videoUrl || '';
  const heroEmbedUrl    = session?.heroEmbedUrl || '';
  const embedSrc        = heroEmbedUrl ? toEmbedUrl(heroEmbedUrl) : null;
  const rawTextVisible  = intro?.rawTextVisible ?? false;
  const rawText         = intro?.rawText || '';

  return (
    <Box sx={{
      minHeight: '100vh',
      height: '100%',
      bgcolor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      px: { xs: 4, md: 8 },
      py: 6,
      userSelect: 'none',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Bakgrundsmedia — välkomstsliden */}
      {isWelcomeSlide && videoUrl && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}>
          <Box component="video" src={videoUrl} autoPlay muted loop playsInline
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.62)' }} />
        </Box>
      )}
      {isWelcomeSlide && !videoUrl && embedSrc && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}>
          <Box component="iframe" src={embedSrc} title="hero" allow="autoplay; fullscreen"
            sx={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.62)' }} />
        </Box>
      )}
      {isWelcomeSlide && !videoUrl && !embedSrc && imageUrl && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::after': {
            content: '""',
            position: 'absolute', inset: 0,
            bgcolor: 'rgba(0,0,0,0.62)',
          },
        }} />
      )}

      {/* Loading */}
      {intro === undefined && (
        <CircularProgress sx={{ color: '#E05500', position: 'relative', zIndex: 1 }} />
      )}

      {/* No intro yet */}
      {intro === null && (
        <Typography sx={{ color: '#555', fontSize: '1rem', position: 'relative', zIndex: 1 }}>
          Väntar på talarstöd…
        </Typography>
      )}

      {/* Waiting for facilitator */}
      {intro !== undefined && intro !== null && !block && (
        <Typography sx={{ color: '#444', fontSize: '1rem', position: 'relative', zIndex: 1 }}>
          Väntar på facilitatorn…
        </Typography>
      )}

      {/* Logo — bottom right */}
      {companyLogo && (
        <Box
          component="img"
          src={companyLogo}
          alt=""
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 28 },
            right: { xs: 16, md: 32 },
            height: { xs: 22, md: 32 },
            maxWidth: 140,
            objectFit: 'contain',
            opacity: 0.45,
            zIndex: 2,
          }}
        />
      )}

      {/* WiFi-badge — bottom left, visas på block 0 */}
      {isWelcomeSlide && wifiSsid && (
        <Box sx={{
          position: 'fixed',
          bottom: { xs: 16, md: 28 },
          left: { xs: 16, md: 32 },
          display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 },
          px: { xs: 1.5, md: 2.5 }, py: { xs: 0.75, md: 1 },
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(6px)',
          zIndex: 2,
        }}>
          <Typography sx={{ color: '#666', fontSize: { xs: '0.6rem', md: '0.7rem' }, fontWeight: 700, letterSpacing: 1 }}>
            WiFi
          </Typography>
          <Typography sx={{ color: '#ccc', fontSize: { xs: '0.72rem', md: '0.88rem' }, fontWeight: 600 }}>
            {wifiSsid}
          </Typography>
          {wifiPassword && (
            <>
              <Typography sx={{ color: '#333', fontSize: '0.6rem' }}>·</Typography>
              <Typography sx={{ color: '#999', fontSize: { xs: '0.72rem', md: '0.88rem' }, fontFamily: 'monospace' }}>
                {wifiPassword}
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Content */}
      {block && (
        <Box
          key={`${intro.currentBlock}`}
          sx={{
            textAlign: 'center',
            maxWidth: 900,
            position: 'relative',
            zIndex: 1,
            animation: 'fadeIn 0.4s ease',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(12px)' },
              to:   { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              fontWeight: 500,
              color: '#ffffff',
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              mb: block.publicQuote ? 4 : 0,
            }}
          >
            {block.publicTitle}
          </Typography>

          {block.publicQuote && (
            <Typography
              sx={{
                fontSize: 'clamp(1rem, 2.5vw, 1.75rem)',
                color: '#E05500',
                fontStyle: 'italic',
                lineHeight: 1.5,
              }}
            >
              {block.publicQuote}
            </Typography>
          )}

          {/* Löptext — visas om facilitatorn valt "Synlig" på blocket */}
          {block.speakerTextVisible && block.speakerText && (
            <Typography
              sx={{
                mt: 4,
                fontSize: 'clamp(0.85rem, 1.8vw, 1.15rem)',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.8,
                maxWidth: 720,
                mx: 'auto',
                textAlign: 'left',
              }}
            >
              {block.speakerText}
            </Typography>
          )}
        </Box>
      )}

      {/* Råtext — visas om facilitatorn valt "Synlig" (hela manusskriptet) */}
      {rawTextVisible && rawText && (
        <Box sx={{
          position: 'fixed', bottom: 60, left: 0, right: 0,
          maxHeight: '25vh', overflowY: 'auto',
          px: { xs: 4, md: 10 }, py: 2,
          bgcolor: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          zIndex: 3,
        }}>
          <Typography sx={{ fontSize: 'clamp(0.72rem, 1.3vw, 0.92rem)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {rawText}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
